import { useRef, useState } from 'react'
import api from '../../services/api.js'

/**
 * Upload a cancelled cheque / passbook page / bank letter and have the account details read off
 * it, instead of copying nine fields by hand off a photo on a phone.
 *
 * The values are SHOWN BEFORE THEY ARE APPLIED. A misread digit in an account number is a
 * payment that goes nowhere, and a form that silently fills itself is a form nobody re-reads —
 * so the reader proposes, the person applies, and every field stays editable afterwards.
 *
 * The file is not stored anywhere; the endpoint reads it and drops it.
 */

// Extractor key → form key. The form calls the country-specific code `iban` because that is the
// column it is stored in; the reader calls it what it is.
const LABELS = {
  account_holder_name: 'Account holder',
  bank_name: 'Bank',
  bank_address: 'Branch address',
  account_number: 'Account number',
  local_code: 'IFSC / routing / IBAN',
  swift_code: 'SWIFT / BIC',
  currency: 'Currency',
  country: 'Country',
}

export default function BankDocumentReader({ onApply }) {
  const fileRef = useRef(null)
  const [busy, setBusy] = useState(false)
  const [fields, setFields] = useState(null)
  const [docType, setDocType] = useState(null)
  const [message, setMessage] = useState(null)

  const handleFile = async (e) => {
    const file = e.target.files?.[0]
    if (!file) return
    setBusy(true); setMessage(null); setFields(null)
    try {
      const fd = new FormData()
      fd.append('file', file)
      const { data } = await api.post('/users/me/banking/read-document', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      if (!data.read) {
        setMessage(data.detail || 'Nothing could be read from that image.')
      } else {
        const { document_type, ...rest } = data.fields
        setDocType(document_type)
        setFields(Object.fromEntries(Object.entries(rest).filter(([, v]) => v)))
      }
    } catch (err) {
      setMessage(err.response?.data?.detail || 'The document could not be read. Type the details in below.')
    } finally {
      setBusy(false)
      if (fileRef.current) fileRef.current.value = ''   // so the same file can be retried
    }
  }

  const apply = () => {
    const { local_code, ...rest } = fields
    onApply({ ...rest, ...(local_code ? { iban: local_code } : {}) })
    setFields(null)
    setMessage('Filled in below — please check every figure against the document before saving.')
  }

  return (
    <div className="rounded-lg border border-dashed border-blue-300 bg-blue-50/50 p-3">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <p className="text-xs font-semibold text-blue-900">Have a cancelled cheque or passbook?</p>
          <p className="text-[11px] text-blue-700">
            Upload a photo and the details below are filled in for you. The image is not saved.
          </p>
        </div>
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          disabled={busy}
          className="text-xs font-semibold px-3 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white shrink-0"
        >
          {busy ? 'Reading…' : 'Upload document'}
        </button>
        <input
          ref={fileRef}
          type="file"
          accept="image/*,application/pdf"
          onChange={handleFile}
          className="hidden"
        />
      </div>

      {message && <p className="mt-2 text-[11px] text-gray-600">{message}</p>}

      {fields && (
        <div className="mt-3 bg-white rounded-lg border border-blue-200 p-3">
          <p className="text-[11px] font-semibold text-gray-700 mb-1.5">
            Read from your {(docType || 'document').toLowerCase()} — check it before using it:
          </p>
          <dl className="text-xs space-y-1">
            {Object.entries(fields).map(([k, v]) => (
              <div key={k} className="flex gap-2">
                <dt className="text-gray-500 w-40 shrink-0">{LABELS[k] || k}</dt>
                <dd className="text-gray-900 font-medium break-all" data-private>{v}</dd>
              </div>
            ))}
          </dl>
          <div className="flex gap-2 mt-3">
            <button type="button" onClick={apply}
              className="text-xs font-semibold px-3 py-1.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white">
              Use these details
            </button>
            <button type="button" onClick={() => setFields(null)}
              className="text-xs px-3 py-1.5 text-gray-600 hover:underline">
              Discard
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
